/**
 * @param ExtendedData   Extra data, i.e. units and groupCodes
 * @param ProductParams  What Product the conf is for, and the same for any Additional Products.
 * @param SyncGroupState The current sync group state
 */
export var CfgProdConfParts;
(function (CfgProdConfParts) {
    CfgProdConfParts[CfgProdConfParts["NoExtra"] = 0] = "NoExtra";
    CfgProdConfParts[CfgProdConfParts["ExtendedData"] = 1] = "ExtendedData";
    CfgProdConfParts[CfgProdConfParts["ProdParams"] = 2] = "ProdParams";
    CfgProdConfParts[CfgProdConfParts["SyncGroupState"] = 4] = "SyncGroupState";
})(CfgProdConfParts || (CfgProdConfParts = {}));
/** Is the newer version of product configuration */
export const isProductConf = (value) => !("selOptions" in value);
/** Is the older version of product configuration */
export const isAdditionalProductConfiguration = (value) => "selOptions" in value;
export const isDtoProductConfAdditional = (value) => "refKey" in value;
// As this has potential to flood the terminal we only inform once
let hasInformedAboutProdParams = false;
let hasInformedAboutGroupCode = false;
let hasInformedAboutUnit = false;
export const convertDtoProductConfToV1 = (conf, silenceWarnings = false) => {
    var _a, _b;
    if (!silenceWarnings && conf.prodParams !== undefined && !hasInformedAboutProdParams) {
        hasInformedAboutProdParams = true;
        console.info("Incoming DtoProductConf contains prodParams. These will be ignored.");
    }
    const result = {
        selOptions: ((_a = conf.features) !== null && _a !== void 0 ? _a : []).map((f) => ({
            code: "!~!",
            next: convertDtoFeatureConfToApiSelection(f, silenceWarnings),
        })),
        additionalProducts: ((_b = conf.additionalProducts) !== null && _b !== void 0 ? _b : []).map((p) => convertDtoProductConfToV1(p, silenceWarnings)),
        selected: true,
    };
    if (isDtoProductConfAdditional(conf)) {
        result.refKey = conf.refKey;
        result.selected = conf.selected;
    }
    return result;
};
export const convertDtoFeatureConfsToSelOptions = (features, silenceWarnings = false) => (features !== null && features !== void 0 ? features : []).map((f) => ({
    code: "!~!",
    next: convertDtoFeatureConfToApiSelection(f, silenceWarnings),
}));
const convertDtoFeatureConfToApiSelection = (feature, silenceWarnings) => {
    const { groupCode, options, unit } = feature;
    if (!silenceWarnings && groupCode !== undefined && !hasInformedAboutGroupCode) {
        hasInformedAboutGroupCode = true;
        console.info("Incoming DtoFeature contains groupCode. It will be ignored.");
    }
    if (!silenceWarnings && unit !== undefined && !hasInformedAboutUnit) {
        hasInformedAboutUnit = true;
        console.info("Incoming DtoFeature contains a unit. It will be ignored.");
    }
    const result = {};
    for (const option of (options !== null && options !== void 0 ? options : []).filter((o) => o.selected)) {
        result[option.code] = convertDtoOptionConfToSelectedOption(option, silenceWarnings);
    }
    return result;
};
const convertDtoOptionConfToSelectedOption = (option, silenceWarnings) => {
    const { features, code, numericValue } = option;
    const selectionTrees = (features !== null && features !== void 0 ? features : []).map((f) => convertDtoFeatureConfToApiSelection(f, silenceWarnings));
    const mergedSelectionTree = {};
    let anyItems = false;
    for (const selectionTree of selectionTrees) {
        if (selectionTree === undefined) {
            continue;
        }
        for (const key of Object.keys(selectionTree)) {
            if (mergedSelectionTree[key] !== undefined) {
                console.warn(`The key (${key}) is already used in the selection tree. Option code: "${code}".`);
                continue;
            }
            mergedSelectionTree[key] = selectionTree[key];
            anyItems = true;
        }
    }
    const selectedOption = {
        code,
        numericValue,
    };
    if (anyItems) {
        selectedOption.next = mergedSelectionTree;
    }
    return selectedOption;
};
const versionedRegex = /^v(\d+)(.+)$/;
const jsonKeyRegex = /"([^"]+)":/g;
const swapForUrlAdaptedRegex = /[-~_{}"]/g;
const shortToLong = new Map();
const longToShort = new Map();
// The replacement scheme here assumes this will only be used for keys
// in DtoProductConf and the knowledge that it contains no one
// one char keys.
for (const [long, short] of [
    ["configuration", "c"],
    ["additionalProducts", "a"],
    ["prodParams", "p"],
    ["refKey", "r"],
    ["selected", "s"],
    ["features", "f"],
    ["code", "d"],
    ["options", "o"],
    ["numericValue", "n"],
    ["groupCode", "g"],
    ["unit", "u"],
    ["syncGroupState", "sgs"],
    ["selectOne", "so"],
    ["selectMany", "sm"],
    ["syncCode", "sc"],
    ["optionCode", "oc"],
]) {
    shortToLong.set(short, long);
    longToShort.set(long, short);
}
/**
 * As certain chars are abundant in JSON but less abundant in the actual data
 * we swap these so that frequent characters do not need to be URL-encoded.
 */
const jsonStringSwapCharsForUrl = (data) => data.replace(swapForUrlAdaptedRegex, (char) => {
    switch (char) {
        case "{":
            return "~";
        case "}":
            return "-";
        case '"':
            return "_";
        case "~":
            return "{";
        case "-":
            return "}";
        case "_":
            return '"';
        default:
            throw new Error(`Unexpected char "${char}" in swap for URL`);
    }
});
const compactDtoProductConfJsonKeys = (data) => data.replace(jsonKeyRegex, (_, key) => { var _a; return `"${(_a = longToShort.get(key)) !== null && _a !== void 0 ? _a : key}":`; });
const expandDtoProductConfJsonKeys = (data) => data.replace(jsonKeyRegex, (_, key) => { var _a; return `"${(_a = shortToLong.get(key)) !== null && _a !== void 0 ? _a : key}":`; });
/**
 * Serializes and compacts the configuration into a format especially suited for URLs
 */
export const dtoProductConfigurationToCompactString = (conf) => "v1" +
    jsonStringSwapCharsForUrl(compactDtoProductConfJsonKeys(JSON.stringify(conf, undefined, "")));
/**
 * Deserializes and inflates the configuration from the compacted format
 */
export const compactStringToDtoProductConf = (versionAndConf) => {
    const match = versionedRegex.exec(versionAndConf);
    if (match === null) {
        throw new Error("Could not match version string");
    }
    const [, version, conf] = match;
    if (version !== "1") {
        throw new Error("Unknown packed URL version");
    }
    if (conf === "") {
        throw new Error("No conf found");
    }
    return JSON.parse(expandDtoProductConfJsonKeys(jsonStringSwapCharsForUrl(conf)));
};
export const stripExtendedDataFromDtoProductConf = (conf, toStrip) => {
    var _a, _b;
    const stripped = {
        features: (_a = conf.features) === null || _a === void 0 ? void 0 : _a.map((f) => stripExtendedDataFromDtoFeatureConf(f, toStrip)),
        additionalProducts: (_b = conf.additionalProducts) === null || _b === void 0 ? void 0 : _b.map(stripExtendedDataFromDtoAdditionalProductConfiguration),
    };
    if ((toStrip & CfgProdConfParts.SyncGroupState) !== CfgProdConfParts.SyncGroupState) {
        stripped.syncGroupState = conf.syncGroupState;
    }
    if ((toStrip & CfgProdConfParts.ProdParams) !== CfgProdConfParts.ProdParams) {
        stripped.prodParams = conf.prodParams;
    }
    return stripped;
};
const stripExtendedDataFromDtoAdditionalProductConfiguration = (conf, toStrip) => (Object.assign(Object.assign({}, stripExtendedDataFromDtoProductConf(conf, toStrip)), { refKey: conf.refKey, selected: conf.selected }));
const stripExtendedDataFromDtoFeatureConf = (conf, toStrip) => {
    var _a;
    const stripped = {
        code: conf.code,
        options: (_a = conf.options) === null || _a === void 0 ? void 0 : _a.map((o) => stripExtendedDataFromDtoOptionConf(o, toStrip)),
    };
    if ((toStrip & CfgProdConfParts.ExtendedData) !== CfgProdConfParts.ExtendedData) {
        // Keep in sync with getDtoConf in CfgFeature
        stripped.groupCode = conf.groupCode;
        stripped.unit = conf.unit;
    }
    return stripped;
};
const stripExtendedDataFromDtoOptionConf = (conf, toStrip) => {
    var _a;
    return ({
        code: conf.code,
        selected: conf.selected,
        numericValue: conf.numericValue,
        features: (_a = conf.features) === null || _a === void 0 ? void 0 : _a.map((f) => stripExtendedDataFromDtoFeatureConf(f, toStrip)),
    });
};
